import React from 'react';
import PropTypes from 'prop-types';
import RelativeDate from '../RelativeDate';

import Card from '../Card';
import Label from '../Label';
import HotjarMasked from '../HotjarMasked';

import './styles.css';
import { calculateNextMajorVersion, getMajorSpaceVersion, isStandardFormat } from '../../utils/spaceVersionUtils';

export function formatDeployWindow(deployWindows) {
  if (!deployWindows) {
    return undefined;
  }
  if (typeof deployWindows === 'string') {
    return [deployWindows];
  }

  const locale =
    window.navigator.userLanguage || window.navigator.language || 'en-us';

  const DAY_SETS = {
    'Every Day': [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday'
    ],
    'Weekdays (M-F)': ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    'Weekends (Excl. Fri)': ['saturday', 'sunday'],
    'Weekends (incl. Fri)': ['friday', 'saturday', 'sunday'],
    'Every-other day (MWF)': ['monday', 'wednesday', 'friday'],
    'Every-other day (TTh)': ['tuesday', 'thursday']
  };

  let deploymentSchedules = {};
  const today = new Date();
  for (const dWindow of deployWindows) {
    const splitDeployWindow = dWindow.split(/ +/);
    const timezone =
      splitDeployWindow.length >= 6 ? splitDeployWindow[5] : 'UTC';
    today.timeZone = timezone;
    const originalDay =
      splitDeployWindow.length >= 1 ? splitDeployWindow[0] : 'unknown';
    const day =
      splitDeployWindow.length >= 1
        ? splitDeployWindow[0].toLowerCase()
        : 'unknown';
    const startTime =
      splitDeployWindow.length >= 3 ? splitDeployWindow[2] : '00:00:00';
    const endTime =
      splitDeployWindow.length >= 5 ? splitDeployWindow[4] : '23:59:59';
    const timeSpan = `${startTime} - ${endTime} ${timezone}`;
    deploymentSchedules[timeSpan] = [
      ...(deploymentSchedules[timeSpan] || []),
      {
        originalDay,
        day,
        timeSpan,
        startTime,
        endTime,
        timezone
      }
    ];
  }

  const outputText = [];
  for (const schedule of Object.values(deploymentSchedules)) {
    const firstScheduledDay = schedule[0];
    const daysInSchedule = schedule.map(s => s.day);
    const friendlyScheduleName = Object.keys(DAY_SETS).find(key => {
      const daySet = DAY_SETS[key];
      return daySet.every(day => daysInSchedule.includes(day));
    });

    if (friendlyScheduleName) {
      outputText.push(`${friendlyScheduleName} ${firstScheduledDay.timeSpan}`);
      const filteredDays = schedule.filter(
        s => !DAY_SETS[friendlyScheduleName].includes(s.day)
      );
      for (const day of filteredDays) {
        outputText.push(`${day.originalDay} ${day.timeSpan}`);
      }
    } else {
      for (const day of schedule) {
        outputText.push(`${day.originalDay} ${day.timeSpan}`);
      }
    }
  }
  return outputText;
}

const ApplianceCard = ({
  appliance_id,
  root_domain_auth,
  last_seen,
  status,
  tenants,
  deployment,
  version,
  space_version,
  platform,
  space_name,
  space_tier,
  cloud,
  region,
  primary_region,
  failover_region,
  nat_list,
  primary_egress_ips,
  failover_egress_ips,
  next_release,
  deployment_window
}) => {
  const title = root_domain_auth;
  const formattedDeployWindow = formatDeployWindow(deployment_window);
  const calculatedNextMajorVersion = calculateNextMajorVersion(space_version);

  // eslint-disable-next-line react/no-multi-comp
  const renderHeader = () => (
    <div>
      <h4 className="card__title">
        {(space_name && (
          <React.Fragment>
            <div>
              <span className="sc-hljs-strong">Environment: </span>
              <span>{space_name}</span>
            </div>
            <HotjarMasked>{title}</HotjarMasked>
          </React.Fragment>
        )) || <HotjarMasked>{title}</HotjarMasked>}
      </h4>
    </div>
  );

  const renderChangelogLink = (spaceVersion) => {
    if (!spaceVersion) {
      return '-';
    }
    const majorSpaceVersion = getMajorSpaceVersion(spaceVersion);
    return (
      <a href={`https://www.auth0.com/changelog?version=${majorSpaceVersion || spaceVersion}`} target="_blank">
        {majorSpaceVersion || spaceVersion}
      </a>
    );
  };

  return (
    <div className="appliance-card">
      <Card fixHeight={560} header={renderHeader()}>
        <div>
          <p>
            {last_seen && (
              <RelativeDate date={last_seen} text="Data last received" />
            )}
          </p>

          <h5 className="card__section-header sc-hljs-strong">
            <i className="card__icon icon-budicon-697" />
            Details
          </h5>
          <ul className="card__list">
            <li>
              <span>
                <span className="sc-hljs-strong">Version:</span>{' '}
                {renderChangelogLink(space_version)}
              </span>
            </li>
            <li>
              <span>
                <span className="sc-hljs-strong">Next Version:</span>{' '}
                {next_release ? renderChangelogLink(next_release) : renderChangelogLink(calculatedNextMajorVersion)}
              </span>
            </li>
            {platform.toLowerCase() !== 'private cloud' && (
              <li className="card--appliance__list-item--deployment">
                <span>
                  <span className="sc-hljs-strong">Deployment Type:</span>{' '}
                  {deployment || '-'}
                </span>
              </li>
            )}
            <li className="card--appliance__list-item--platform">
              <span>
                <span className="sc-hljs-strong">Platform:</span>{' '}
                {platform || '-'}
              </span>
            </li>
          </ul>

          {platform.toLowerCase() === 'private cloud' ? (
            <div>
              <h5 className="card__section-header sc-hljs-strong">
                <i className="card__icon icon-budicon-156" />
                Cloud Details
              </h5>
              <ul className="card__list">
                {cloud && (
                  <li className="card--appliance__list-item--cloud">
                    <span>
                      <span className="sc-hljs-strong">
                        Cloud Provider:
                      </span>{' '}
                      {cloud}
                    </span>
                  </li>
                )}
                {space_tier && (
                  <li className="card--appliance__list-item--space-tier">
                    <span>
                      <span className="sc-hljs-strong">
                        Environment Type:
                      </span>{' '}
                      {space_tier}
                    </span>
                  </li>
                )}
                {region && (
                  <li className="card--appliance__list-item--region">
                    <span>
                      <span className="sc-hljs-strong">
                        Active Region:
                      </span>{' '}
                      {region}
                    </span>
                  </li>
                )}
                {primary_region && (
                  <li className="card--appliance__list-item--region">
                    <span>
                      <span className="sc-hljs-strong">Primary:</span>{' '}
                      {primary_region}
                    </span>
                  </li>
                )}
                {failover_region && (
                  <li className="card--appliance__list-item--failover">
                    <span>
                      <span className="sc-hljs-strong">Failover:</span>{' '}
                      {failover_region}
                    </span>
                  </li>
                )}
                {formattedDeployWindow && (
                  <li className="card--appliance__list-item--failover">
                    <span>
                      <span className="sc-hljs-strong">
                        Deployment Window(s):
                      </span>{' '}
                      {formattedDeployWindow.map((line, i) => {
                        return <div key={i}>{line}</div>;
                      })}
                    </span>
                  </li>
                )}
                {nat_list &&
                nat_list.length > 0 && (
                  <li>
                    <span className="sc-hljs-strong">NAT IP List:</span>
                    <ul>
                      {nat_list.map((nat, i) => (
                        <li key={`${nat}-${appliance_id}-${i}`}>
                          <span>{nat}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
                {primary_egress_ips &&
                primary_egress_ips.length > 0 && (
                  <li>
                    <span className="sc-hljs-strong">Primary Egress IPs:</span>
                    <ul>
                      {primary_egress_ips.split(',').map((ip, i) => (
                        <li key={`${ip}-${appliance_id}-${i}`}>
                          <span>{ip}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
                {failover_egress_ips &&
                failover_egress_ips.length > 0 && (
                  <li>
                    <span className="sc-hljs-strong">Failover Egress IPs:</span>
                    <ul>
                      {failover_egress_ips.split(',').map((ip, i) => (
                        <li key={`${ip}-${appliance_id}-${i}`}>
                          <span>{ip}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
              </ul>
            </div>
          ) : (
            ''
          )}

          <h5 className="card--appliance__section-header">
            <i className="card--appliance__icon icon-budicon-661" />
            <span className="sc-hljs-strong">Tenants:</span>
          </h5>
          <ul className="card--appliance__list">
            <li>
              <span className="sc-hljs-strong">Total:</span> {tenants.length}
            </li>
            {tenants.map((tenant, i) => (
              <li key={`${appliance_id}-${i}`}>
                <HotjarMasked isInline>{tenant.appliance_tenant}</HotjarMasked>
              </li>
            ))}
          </ul>
        </div>
      </Card>
    </div>
  );
};

ApplianceCard.propTypes = {
  appliance_id: PropTypes.string.isRequired,
  root_domain_auth: PropTypes.string.isRequired,
  last_seen: PropTypes.string.isRequired,
  dev_staging_prod: PropTypes.string,
  deployment: PropTypes.string,
  version: PropTypes.string,
  space_version: PropTypes.string,
  status: PropTypes.string,
  tenants: PropTypes.array,
  platform: PropTypes.string,
  space_name: PropTypes.string,
  space_tier: PropTypes.string,
  cloud: PropTypes.string,
  region: PropTypes.string,
  primary_region: PropTypes.string,
  failover_region: PropTypes.string,
  nat_list: PropTypes.array,
  next_release: PropTypes.string,
  deployment_window: PropTypes.arrayOf(PropTypes.string),
  primary_egress_ips: PropTypes.string,
  failover_egress_ips: PropTypes.string
};

export default ApplianceCard;
